import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { t } from './sprache'
import type { Sprachschluessel } from './sprache'

/**
 * Das Kontextmenü an einer Berichtszeile.
 *
 * **Drei Wege zum selben Menü**, und das ist keine Bequemlichkeit:
 *
 * - **Rechtsklick** — was ein Anwender aus Excel mitbringt.
 * - **Kontextmenü-Taste / Umschalt+F10** auf der fokussierten Zeile. Der
 *   Browser löst dafür dasselbe `contextmenu`-Ereignis aus wie für die Maus,
 *   ohne dass wir etwas hinzufügen müssten — vorausgesetzt, die Zeile ist
 *   überhaupt fokussierbar. Genau deshalb trägt sie `tabIndex`.
 * - **Ein Knopf am Zeilenanfang**, der beim Überfahren und bei Fokus erscheint.
 *   Für Touch, wo es keinen Rechtsklick gibt.
 *
 * Eine Funktion, die nur die Maus kennt, gibt es in CoKo nicht (R143, L9/L10).
 * Dass L10 erst in V2 abgenommen wird, ändert daran nichts: Wer damit in V2
 * anfängt, baut die Oberfläche zweimal.
 *
 * **Im Menü führen die Pfeiltasten**, Pos1 und Ende springen an die Ränder,
 * Escape schließt. Seit es Untermenüs gibt, führt **Pfeil rechts in eine
 * Gruppe hinein und Pfeil links wieder heraus** (R576) — das ist die
 * Bedienung, die ein Menü überall hat; ein Menü, das nur mit Tab zu
 * durchlaufen ist, fühlt sich an wie ein Formular.
 *
 * **Die Gruppen sind durch Linien getrennt** (7.3 Abschnitt 3): der
 * Wertenachweis dieser Zelle, dann die **drei Ansichtsgruppen hinter je einem
 * Punkt**, dann, abgesetzt, was schreibt.
 */

/** Was jeder Punkt trägt — ob er nun etwas tut oder irgendwohin führt. */
type Beschriftung = {
  art?: 'punkt'
  /** Der Text aus der Sprachdatei — oder ein fertiger Text, wo er aus Stammdaten kommt. */
  schluessel?: Sprachschluessel
  text?: string
  /** Ein Punkt, der gerade nichts bewirkt, wird nicht angeboten. */
  wenn?: boolean
}

export type Menuepunkt =
  | (Beschriftung & {
      /** Ein Punkt aus einer Wahl — der eingestellte trägt ein Häkchen. */
      wahl?: boolean
      gewaehlt?: boolean
      tun: () => void
      adresse?: never
      unter?: never
    })
  /**
   * **Ein Punkt, der an einen Ort führt, ist ein Link** (R550) — **geöffnet
   * wird er aber von der Anwendung und nicht vom Browser.**
   *
   * Beides hat seinen Grund. Ein Link **trägt seine Adresse**: Sie steht in
   * der Statuszeile, das eigene Kontextmenü des Browsers steht wieder auf dem
   * Punkt, und was die Leiste dem Anwender am Eintrag genommen hat, bekommt er
   * hier zurück. Geöffnet wird er trotzdem mit `window.open`, weil **nur ein
   * `window.open` ein Fenster aufmacht** — ein Link öffnet bestenfalls einen
   * Tab, und in einer eingebetteten Ansicht öffnet er gar nichts, sondern
   * wechselt den Ort im Fenster, in dem man steht.
   *
   * **Genau das darf nicht passieren** (Entscheidung des Auftraggebers vom
   * 2026-09-15): Der Klick nimmt dem Link die eigene Navigation *immer* ab.
   * Geht kein Fenster auf, geht eben keines auf — aber der Bildschirm, auf den
   * der Anwender gerade sieht, bleibt stehen.
   */
  | (Beschriftung & {
      /** Wohin der Punkt führt — vollständig, damit sie teilbar ist (R156). */
      adresse: string
      /**
       * Gesetzt heißt: **erst ein eigenes Fenster versuchen**, mit diesen
       * Maßen. Geht es nicht auf, bleibt der Link, was er ist, und der Ort
       * kommt im Tab. Die Maße entscheidet, wer den Punkt stellt.
       */
      fenster?: string
      tun?: never
      wahl?: never
      gewaehlt?: never
      unter?: never
    })
  /**
   * **Ein Punkt, der eine ganze Gruppe öffnet** (R576, 7.3 Abschnitt 3).
   *
   * Er tut selbst nichts — er hält eine Gruppe zusammen, die sonst als sechs
   * gleichartige Zeilen dastünde und das Menü über den Fensterrand schöbe.
   * **Was schreibt, steht nie darin**: Ein Untermenü kostet einen Klick und
   * nimmt den Punkt aus dem Blick, und das darf es nur mit denen tun, die
   * bloß die Ansicht ändern.
   */
  | (Beschriftung & {
      unter: Menuepunkt[]
      tun?: never
      adresse?: never
      wahl?: never
      gewaehlt?: never
    })
  | { art: 'trenner' }

/** Die Punkte einer Ebene — und zwar nur ihre eigenen, nicht die der Gruppe daneben. */
function punkteIn(ebene: HTMLElement | null): HTMLElement[] {
  return [...(ebene?.querySelectorAll<HTMLElement>(':scope > button, :scope > a') ?? [])]
}

export function Zeilenmenue({
  x,
  y,
  punkte,
  aufSchliessen,
}: {
  x: number
  y: number
  punkte: Menuepunkt[]
  aufSchliessen: () => void
}) {
  const rahmen = useRef<HTMLDivElement>(null)
  const unterrahmen = useRef<HTMLDivElement>(null)
  /** Welche Gruppe gerade offen steht — höchstens eine. */
  const [zweig, setZweig] = useState<number | null>(null)
  /** Ob der Fokus mitwandern soll: mit der Tastatur ja, beim Überfahren nicht. */
  const fokusInZweig = useRef(false)
  const [ort, setOrt] = useState({ links: x, oben: y })
  const [unterort, setUnterort] = useState({ links: x, oben: y })

  const sichtbar = bereinigt(punkte)

  /*
    **Am Mauszeiger, aber nie über den Rand hinaus** — und **gemessen statt
    geschätzt** (R576).

    Bis zum 2026-09-16 rechnete diese Stelle mit 34 Pixeln je Punkt und zählte
    dabei nur die Punkte. Die Trennlinien und die Polsterung des Rahmens kamen
    darin nicht vor: Bei fünf Linien fehlten 59 Pixel, und das Menü setzte sich
    um genau diesen Betrag zu tief auf. **Was dabei unter den Fensterrand
    rutscht, ist immer der letzte Eintrag** — und unten stehen die Punkte, die
    schreiben (R494).

    Ein Maß, das aus der Zahl der Punkte gerechnet wird, ist eine Kopie des
    Stylesheets an einer zweiten Stelle, und sie veraltet beim ersten
    Polsterwert, den jemand ändert. Der Rahmen weiß selbst, wie hoch er ist;
    gefragt wird deshalb er. Gemessen wird **vor dem Zeichnen**
    (`useLayoutEffect`), sonst sähe der Anwender den ersten Ort aufblitzen.
  */
  useLayoutEffect(() => {
    const k = rahmen.current?.getBoundingClientRect()
    if (!k) {
      return
    }
    const links = Math.max(4, Math.min(x, window.innerWidth - k.width - 4))
    const oben = Math.max(4, Math.min(y, window.innerHeight - k.height - 4))
    // Derselbe Ort gibt denselben Zustand zurück — sonst liefe die Messung,
    // die bei jedem Zeichnen läuft, gegen sich selbst.
    setOrt((alt) => (alt.links === links && alt.oben === oben ? alt : { links, oben }))
  })

  /*
    **Die Gruppe steht neben ihrem Punkt, nicht über ihm** — rechts, solange
    dort Platz ist, sonst links davon. Dieselbe Messung wie beim Rahmen: Wohin
    sie passt, weiß nur, wer beide Maße kennt.
  */
  useLayoutEffect(() => {
    if (zweig === null) {
      return
    }
    const knopf = rahmen.current
      ?.querySelector<HTMLElement>(`[data-zweig="${zweig}"]`)
      ?.getBoundingClientRect()
    const rand = rahmen.current?.getBoundingClientRect()
    const k = unterrahmen.current?.getBoundingClientRect()
    if (!knopf || !rand || !k) {
      return
    }
    const links =
      rand.right + k.width + 4 <= window.innerWidth
        ? rand.right - 2
        : Math.max(4, rand.left - k.width + 2)
    const oben = Math.max(4, Math.min(knopf.top - 4, window.innerHeight - k.height - 4))
    setUnterort((alt) => (alt.links === links && alt.oben === oben ? alt : { links, oben }))
  })

  // Der Fokus wandert ins Menü, sonst wäre es mit der Tastatur geöffnet und
  // dann nicht erreichbar — der häufigste Fehler bei genau dieser Bauform.
  useEffect(() => {
    punkteIn(rahmen.current)[0]?.focus()
  }, [])

  // Mit der Tastatur geöffnet, nimmt die Gruppe den Fokus mit; beim Überfahren
  // mit der Maus bleibt er, wo er ist.
  useLayoutEffect(() => {
    if (zweig !== null && fokusInZweig.current) {
      fokusInZweig.current = false
      punkteIn(unterrahmen.current)[0]?.focus()
    }
  }, [zweig])

  useEffect(() => {
    function zweigknopf(): HTMLElement | null {
      return zweig === null
        ? null
        : (rahmen.current?.querySelector<HTMLElement>(`[data-zweig="${zweig}"]`) ?? null)
    }

    /** Aus der Gruppe heraus und zurück auf den Punkt, der sie geöffnet hat. */
    function zurueck() {
      const knopf = zweigknopf()
      setZweig(null)
      knopf?.focus()
    }

    function beiTaste(e: KeyboardEvent) {
      const imZweig = unterrahmen.current?.contains(document.activeElement) ?? false

      if (e.key === 'Escape') {
        e.preventDefault()
        // **Escape nimmt eine Ebene zurück, nicht gleich das ganze Menü.** Wer
        // in die falsche Gruppe geraten ist, will heraus und nicht von vorn.
        if (imZweig) {
          zurueck()
        } else {
          aufSchliessen()
        }
        return
      }

      // **Knöpfe und Links in einer Liste**: Seit ein Punkt auch ein Link sein
      // kann (R550), liefen die Pfeiltasten sonst an ihm vorbei — und der erste
      // Punkt des Leistenmenüs ist genau einer. Gezählt wird nur die Ebene, in
      // der der Fokus steht; die Gruppe daneben ist eine eigene.
      const liste = punkteIn(imZweig ? unterrahmen.current : rahmen.current)
      if (liste.length === 0) {
        return
      }

      const stelle = liste.findIndex((k) => k === document.activeElement)

      if (e.key === 'ArrowRight') {
        const auf = liste[stelle]?.dataset.zweig
        if (auf !== undefined) {
          e.preventDefault()
          fokusInZweig.current = true
          setZweig(Number(auf))
        }
        return
      }

      if (e.key === 'ArrowLeft') {
        if (imZweig) {
          e.preventDefault()
          zurueck()
        }
        return
      }

      /*
        **Die Leertaste gehört zum Menüpunkt, auch wenn er ein Link ist.** Auf
        einem Knopf löst der Browser sie selbst aus, auf einem Link scrollt er
        damit — und ein Punkt, der auf Enter etwas tut und auf Leertaste
        wegrollt, ist für den, der mit der Tastatur arbeitet, kaputt (R143).
      */
      if (e.key === ' ' && liste[stelle]?.tagName === 'A') {
        e.preventDefault()
        liste[stelle]!.click()
        return
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const schritt = e.key === 'ArrowDown' ? 1 : -1
        liste[(stelle + schritt + liste.length) % liste.length]?.focus()
      } else if (e.key === 'Home') {
        e.preventDefault()
        liste[0]?.focus()
      } else if (e.key === 'End') {
        e.preventDefault()
        liste[liste.length - 1]?.focus()
      }
    }

    function beiKlickAussen(e: MouseEvent) {
      if (!rahmen.current?.contains(e.target as Node)) {
        aufSchliessen()
      }
    }

    window.addEventListener('keydown', beiTaste)
    window.addEventListener('mousedown', beiKlickAussen)
    return () => {
      window.removeEventListener('keydown', beiTaste)
      window.removeEventListener('mousedown', beiKlickAussen)
    }
  }, [aufSchliessen, zweig])

  /** Die Beschriftung eines Punkts — ein fertiger Text vor dem Sprachschlüssel. */
  function wort(p: Beschriftung): string {
    return p.text ?? (p.schluessel ? t(p.schluessel) : '')
  }

  /** Ein Punkt, der etwas tut oder irgendwohin führt — in beiden Ebenen derselbe. */
  function punkt(p: Menuepunkt, i: number) {
    if (p.art === 'trenner') {
      return <hr key={`trenner-${i}`} className="trenner" role="separator" />
    }

    if (p.unter !== undefined) {
      /*
        **Der Punkt, der eine Gruppe öffnet** (R576). Er schließt das Menü
        nicht — er wechselt die Ebene, und beim Überfahren mit der Maus tut er
        es ohne Klick, so wie ein Anwender es kennt.
      */
      return (
        <button
          key={p.schluessel ?? p.text ?? i}
          type="button"
          role="menuitem"
          data-zweig={i}
          aria-haspopup="menu"
          aria-expanded={zweig === i}
          onMouseEnter={() => setZweig(i)}
          onClick={() => {
            fokusInZweig.current = true
            setZweig(zweig === i ? null : i)
          }}
        >
          <span className="haken" aria-hidden="true" />
          {wort(p)}
          <span className="zweigpfeil" aria-hidden="true">
            ▸
          </span>
        </button>
      )
    }

    if (p.adresse !== undefined) {
      /*
        **Ein Ziel wird als Link angeboten und nicht als Knopf** (R550).

        Damit hängt das Öffnen nicht mehr an der Popup-Sperre: Ein Klick
        auf einen Link ist für den Browser eine Navigation und keine
        Fensteranforderung. Zwei Dinge kommen umsonst mit — der Anwender
        sieht das Ziel in der Statuszeile, und sein eigenes Kontextmenü
        steht auf dem Punkt wieder zur Verfügung.
      */
      return (
        <a
          key={p.schluessel ?? p.text ?? i}
          role="menuitem"
          href={p.adresse}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            /*
              **Zuerst die eigene Navigation abbestellen.** Sie ist der
              einzige Weg, auf dem dieser Punkt im Ausgangsfenster landen
              könnte — und ein Punkt, der den Bildschirm wegnimmt, statt
              einen zweiten danebenzustellen, tut das Gegenteil dessen,
              wofür es ihn gibt.
            */
            e.preventDefault()

            /*
              `fenster` trägt die Maße und macht aus dem Tab ein Fenster;
              ohne sie öffnet der Browser einen Tab. Was dabei herauskommt,
              entscheidet er — **unser Teil ist, dass nichts Falsches
              passiert, wenn er nichts hergibt.**
            */
            window.open(p.adresse, '_blank', p.fenster)
            aufSchliessen()
          }}
        >
          <span className="haken" aria-hidden="true" />
          {wort(p)}
        </a>
      )
    }

    return (
      <button
        key={p.schluessel ?? p.text ?? i}
        type="button"
        role={p.wahl ? 'menuitemradio' : 'menuitem'}
        aria-checked={p.wahl ? (p.gewaehlt ?? false) : undefined}
        onClick={() => {
          p.tun()
          aufSchliessen()
        }}
      >
        <span className="haken" aria-hidden="true">
          {p.gewaehlt ? '✓' : ''}
        </span>
        {wort(p)}
      </button>
    )
  }

  const offen = zweig === null ? undefined : sichtbar[zweig]

  return (
    <div ref={rahmen} className="zeilenmenue" role="menu" style={{ left: ort.links, top: ort.oben }}>
      {sichtbar.map((p, i) => punkt(p, i))}

      {/* Höchstens eine Gruppe steht offen — sie liegt neben dem Menü, nicht darin. */}
      {offen && offen.art !== 'trenner' && offen.unter !== undefined && (
        <div
          ref={unterrahmen}
          className="untermenue"
          role="menu"
          style={{ left: unterort.links, top: unterort.oben }}
          onMouseLeave={() => {
            // Der Fokus soll nicht ins Leere fallen: Wer mit der Tastatur darin
            // steht, behält die Gruppe, auch wenn die Maus sie verlässt.
            if (!unterrahmen.current?.contains(document.activeElement)) {
              setZweig(null)
            }
          }}
        >
          {offen.unter.map((u, j) => punkt(u, j))}
        </div>
      )}
    </div>
  )
}

/**
 * Nur die Punkte, die etwas bewirken — und keine Linie, die nichts trennt:
 * kein Trenner am Anfang, keiner am Ende, keine zwei hintereinander.
 *
 * **Eine leere Gruppe ist selbst ein Punkt, der nichts bewirkt** (R576): Wo
 * die Zeile keine Achse mehr offen hat, steht kein *Aufriss dieser Zeile* da,
 * das beim Anklicken ein leeres Kästchen aufmacht.
 */
export function bereinigt(punkte: Menuepunkt[]): Menuepunkt[] {
  const ergebnis: Menuepunkt[] = []

  for (const p of punkte) {
    if (p.art === 'trenner') {
      if (ergebnis.length > 0 && ergebnis[ergebnis.length - 1]!.art !== 'trenner') {
        ergebnis.push(p)
      }
      continue
    }

    if (p.wenn === false) {
      continue
    }

    if (p.unter !== undefined) {
      const inhalt = bereinigt(p.unter)
      if (inhalt.length > 0) {
        ergebnis.push({ ...p, unter: inhalt })
      }
      continue
    }

    ergebnis.push(p)
  }

  while (ergebnis.length > 0 && ergebnis[ergebnis.length - 1]!.art === 'trenner') {
    ergebnis.pop()
  }

  return ergebnis
}
